import i18n from '@/i18n';

import { formatQuantityUnit } from '../../utils/formatQuantityUnit';
import { isSubResourcePricingEnabled } from '../../utils/subResourcePricing';
import type { VendorDeviceBillOption, VendorDeviceSubResourceConfig } from './types';

function parseBillModelConfig(config: string) {
  try {
    return JSON.parse(config);
  } catch {
    return { price: 0, unit: 'MONTH' };
  }
}

function resolveTimeUnitLabel(timeUnit: string, lang?: string, timeUnits?: { value: string; label: string }[]): string {
  const fromList = timeUnits?.find((item) => item.value === timeUnit)?.label;
  if (fromList) return fromList;
  if (lang === 'zh-cn') {
    return i18n.t(`product:${timeUnit.toLowerCase()}`, { defaultValue: timeUnit });
  }
  return timeUnit;
}

export interface BillReferencePriceInput {
  billConfig?: unknown;
  billModel?: unknown;
  resource?: { unit?: string };
}

function formatUnitPriceLine({
  configRaw,
  resourceUnit,
  lang,
  timeUnits,
  currencySymbol,
}: {
  configRaw?: string;
  resourceUnit?: string;
  lang?: string;
  timeUnits?: { value: string; label: string }[];
  currencySymbol: string;
}): { text: string; totalPrice?: string } {
  if (!configRaw) return { text: '-' };
  const config = parseBillModelConfig(String(configRaw));
  const price = config.pricePerUnit || 0;
  let unit = resourceUnit || config.quantityUnit || '';
  unit = formatQuantityUnit(unit, lang);
  const timeUnit = config.timeUnit || 'MONTH';
  const timeLabel = resolveTimeUnitLabel(timeUnit, lang, timeUnits);
  const unitPart = unit ? `${unit} / ${lang === 'zh-cn' ? timeLabel : timeUnit}` : lang === 'zh-cn' ? timeLabel : timeUnit;
  const totalPrice = Number(price)?.toFixed(2);
  return {
    totalPrice,
    text: unitPart ? `${currencySymbol}${totalPrice} / ${unitPart}` : `${currencySymbol}${totalPrice}`,
  };
}

/** 厂商设备参考价展示（插件内纯净版，不依赖 pages） */
export function formatBillReferencePrice({
  sku,
  lang,
  timeUnits,
}: {
  sku: BillReferencePriceInput;
  lang?: string;
  timeUnits?: { value: string; label: string }[];
}): { unit?: string; totalPrice?: string; currency?: string } {
  if (!sku.billConfig) return {};
  const billModel = sku.billModel as { currency?: string } | undefined;
  const { currency } = billModel || {};
  const config = parseBillModelConfig(String(sku.billConfig));
  const price = config.pricePerUnit || 0;
  let unit = sku.resource?.unit || config.quantityUnit || '';
  unit = formatQuantityUnit(unit, lang);
  const timeUnit = config.timeUnit || 'MONTH';
  const timeLabel = resolveTimeUnitLabel(timeUnit, lang, timeUnits);
  const unit1 = unit ? `${unit} / ${timeLabel}` : timeLabel || '';
  const unit2 = unit ? `${unit} / ${timeUnit}` : timeUnit || '';

  return {
    unit: lang === 'zh-cn' ? unit1 : unit2,
    totalPrice: Number(price)?.toFixed(2),
    currency: currency === 'CNY' ? '¥' : '$',
  };
}

export function formatSubResourceReferenceLines(
  configs: VendorDeviceSubResourceConfig[] | undefined,
  lang?: string,
  timeUnits?: { value: string; label: string }[],
  currencySymbol = '¥',
): { code: string; text: string }[] {
  if (!configs?.length) return [];
  return configs.map((sub) => {
    const line = formatUnitPriceLine({
      configRaw: sub.config,
      lang,
      timeUnits,
      currencySymbol,
    });
    return {
      code: sub.subResourceCode,
      text: line.text,
    };
  });
}

/** 选项参考价：支持子资源计价多行展示 */
export function formatOptionReferencePriceDisplay(
  option: VendorDeviceBillOption,
  lang?: string,
  timeUnits?: { value: string; label: string }[],
): { single?: string; lines?: { code: string; text: string }[] } {
  const currencySymbol = '¥';
  if (isSubResourcePricingEnabled(option)) {
    const lines = formatSubResourceReferenceLines(option.subResourceConfigs, lang, timeUnits, currencySymbol);
    if (lines.length) return { lines };
  }
  if (!option.billConfig) return { single: '-' };
  const { currency, totalPrice, unit } = formatBillReferencePrice({
    sku: {
      billConfig: option.billConfig,
      billModel: { currency: 'CNY' },
      resource: option.resourceUnit ? { unit: option.resourceUnit } : undefined,
    },
    lang,
    timeUnits,
  });
  if (!totalPrice) return { single: '-' };
  return { single: unit ? `${currency}${totalPrice} / ${unit}` : `${currency}${totalPrice}` };
}
